import React from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useAppSelector } from '../../../../hooks/reduxHooks';

/*
 * Cáscara de los drawers de carga del informe SVCC. Es propia del módulo y no
 * toca `DrawerContainer` ni `DrawerHeader`, que usan otras 30 pantallas.
 *
 * Se arma como: <DrawerSvcc> → <DrawerSvccEncabezado> + <DrawerSvccCuerpo>
 * (con <DrawerSvccSeccion>) + <DrawerSvccPie>. Cada formulario conserva sus
 * campos, validaciones y handlers; sólo cambia dónde los pone.
 */

/** Alto del AppBar: el drawer arranca debajo, no lo tapa (ver `AppBar.tsx`). */
const ALTO_APPBAR = 64;

export const COLORES_DRAWER = {
    borde: '#E0EBED',
    texto: '#1E272E',
    textoSuave: '#6B777C',
    fondoSuave: '#F3F8F8'
};

interface DrawerSvccProps {
    open: boolean;
    onClose: () => void;
    /** 560 para formularios chicos, 720 para los de varias pestañas. */
    ancho?: number;
    children: React.ReactNode;
}

export const DrawerSvcc = ({ open, onClose, ancho = 560, children }: DrawerSvccProps) => {
    const { isMobile } = useIsMobile();
    return (
        <Drawer
            anchor="right"
            open={open}
            /*
             * Escape y el clic afuera no cierran: son formularios largos y se
             * perdía todo lo cargado sin preguntar. Se cierra con la X o Cancelar,
             * que llaman a `onClose` directamente.
             */
            onClose={(_evento, motivo) => {
                if (motivo === 'escapeKeyDown' || motivo === 'backdropClick') return;
                onClose();
            }}
            PaperProps={{
                sx: {
                    top: ALTO_APPBAR,
                    height: `calc(100% - ${ALTO_APPBAR}px)`,
                    width: isMobile ? '100vw' : ancho,
                    borderRadius: isMobile ? 0 : '16px 0 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '-8px 0 32px rgba(0,0,0,0.16)'
                }
            }}
        >
            {children}
        </Drawer>
    );
};

/**
 * Carga dentro del drawer, mientras se guarda. Reemplaza a `BackdropComponent`,
 * que cubre toda la página y dejaba el spinner centrado en la pantalla, medio
 * tapado por el borde del drawer. El papel del drawer es `position: fixed`, así
 * que el overlay absoluto queda contenido en él.
 */
export const DrawerSvccCargando = ({ cargando }: { cargando: boolean }) =>
    cargando ? (
        <Box
            role="progressbar"
            aria-label="Guardando"
            sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.6)'
            }}
        >
            <CircularProgress />
        </Box>
    ) : null;

interface EncabezadoProps {
    titulo: React.ReactNode;
    /** Nombre del paso del informe, para la línea de contexto. */
    paso?: string;
    onCerrar: () => void;
    /** Debajo del título: por ejemplo, las pestañas del drawer. */
    children?: React.ReactNode;
}

export const DrawerSvccEncabezado = ({ titulo, paso, onCerrar, children }: EncabezadoProps) => {
    const idInforme = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const contexto = [idInforme ? `Informe ${idInforme}` : null, paso].filter(Boolean).join(' · ');

    return (
        <Box
            component="header"
            sx={{
                px: 3,
                pt: 1.75,
                pb: 1.5,
                borderBottom: `1px solid ${COLORES_DRAWER.borde}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                flexShrink: 0
            }}
        >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
                <Box flex={1} minWidth={0}>
                    <Typography
                        component="h2"
                        data-testid="drawer-title"
                        sx={{
                            fontSize: 18,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            color: COLORES_DRAWER.texto
                        }}
                    >
                        {titulo}
                    </Typography>
                    {contexto && (
                        <Typography
                            variant="body2"
                            sx={{ color: COLORES_DRAWER.textoSuave, mt: 0.25 }}
                        >
                            {contexto}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="Cerrar"
                    data-testid="drawer-icon-button-arrow-back"
                    onClick={onCerrar}
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        bgcolor: COLORES_DRAWER.fondoSuave,
                        '&:hover': { bgcolor: COLORES_DRAWER.borde }
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18, color: '#3D4A50' }} />
                </IconButton>
            </Box>
            {children}
        </Box>
    );
};

/**
 * Área que scrollea. Ajusta los campos de MUI sólo dentro del drawer: bordes
 * redondeados y sin el margen vertical (`margin="normal"` de CustomField) que
 * los separaba de más; el espaciado lo da la grilla. No toca alturas ni
 * etiquetas: CustomField ya los dibuja en tamaño "small".
 */
export const DrawerSvccCuerpo = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: 3,
            py: 1.75,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            '& .MuiFormControl-root.MuiFormControl-marginNormal, & .MuiFormControl-root.MuiFormControl-marginDense':
                { mt: 0, mb: 0 },
            '& .MuiTextField-root, & .MuiFormControl-root': { width: '100%' },
            // index.css fija .MuiInputBase-root en min-height 3.7rem (~52px) y radio
            // 5px con !important: acá se baja al alto chico de MUI (40px) y se
            // redondea. Es min-height, así que un select múltiple con chips crece.
            '& .MuiInputBase-root': { minHeight: '40px', borderRadius: '8px !important' },
            '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' },
            // Los selects (Autocomplete de CustomField) bajan la etiqueta con top: 10px,
            // calculado para 52px. Vacíos, la deja centrar MUI en los 40px.
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { marginTop: '2px' },
            '& .MuiAutocomplete-root .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                top: '2px'
            }
        }}
    >
        {children}
    </Box>
);

export const DrawerSvccSeccion = ({
    titulo,
    children
}: {
    titulo?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <Box
        component="section"
        sx={{
            border: `1px solid ${COLORES_DRAWER.borde}`,
            borderRadius: '12px',
            p: 1.5,
            flexShrink: 0
        }}
    >
        {titulo && (
            <Typography
                component="h3"
                sx={{ fontSize: 14, fontWeight: 700, color: COLORES_DRAWER.texto, mb: 1 }}
            >
                {titulo}
            </Typography>
        )}
        {children}
    </Box>
);

/**
 * Aviso informativo arriba del formulario, en un tinte del color de la
 * organización (theme): no un verde fijo, que no es el de todas.
 */
export const DrawerSvccAyuda = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            px: 1.75,
            py: 1.5,
            borderRadius: '10px',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            color: 'primary.main',
            fontSize: 13,
            flexShrink: 0
        }}
    >
        {children}
    </Box>
);

interface PieProps {
    onCancelar: () => void;
    onConfirmar?: () => void;
    textoCancelar: React.ReactNode;
    textoConfirmar?: React.ReactNode;
    /** Texto chico a la izquierda: paso, campos obligatorios, etc. */
    nota?: React.ReactNode;
    cargando?: boolean;
    deshabilitado?: boolean;
    /** Sin acción principal (por ejemplo, en sólo lectura). */
    sinConfirmar?: boolean;
}

export const DrawerSvccPie = ({
    onCancelar,
    onConfirmar,
    textoCancelar,
    textoConfirmar,
    nota,
    cargando = false,
    deshabilitado = false,
    sinConfirmar = false
}: PieProps) => (
    <Box
        component="footer"
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${COLORES_DRAWER.borde}`,
            bgcolor: '#FFFFFF',
            flexShrink: 0
        }}
    >
        <Typography
            variant="body2"
            sx={{ flex: 1, color: COLORES_DRAWER.textoSuave, fontSize: 13 }}
        >
            {nota}
        </Typography>
        <Button
            variant="outlined"
            onClick={onCancelar}
            data-testid="drawer-cancelar"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, height: 40 }}
        >
            {textoCancelar}
        </Button>
        {!sinConfirmar && (
            <Button
                variant="contained"
                onClick={onConfirmar}
                disabled={deshabilitado || cargando}
                data-testid="drawer-confirmar"
                startIcon={cargando ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    px: 2.5
                }}
            >
                {textoConfirmar}
            </Button>
        )}
    </Box>
);
